import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { filter, find, isEmpty, map, trim, get, split, head, last } from 'lodash';
import classnames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import { capitalizeFirstLetter } from '@lib/services/stringsService';

import { PopperChatActivityAssignees } from 'deprecated/query-triggers';
import { setTextAreaCaretPosition } from 'utils/helpers';

import InputField from './InputField';

const styles = (theme) => {
  const {
    palette: { action, error },
    spacing: { unit },
  } = theme;

  return {
    error: {},
    helperText: {
      color: action.active,
      fontSize: 10,
      fontWeight: 500,
      '&$error': {
        color: `${error.main} !important`,
      },
    },
    squigglyMenuPopper: {
      marginBottom: unit * 2,
    },
    wrapper: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      position: 'relative',
    },
  };
};

class EnhancedTextField extends PureComponent {
  state = {
    markupRecentlyAdded: false,
    mentions: [],
    mentionsMenuAnchor: false,
    searchText: '',
    selectedIndex: 0,
    startPosition: null,
    templatesMenuAnchor: null,
    textareaInfo: {},
  };

  componentWillReceiveProps(nextProps) {
    const { externalTemplateAdded } = this.props;

    if (nextProps.externalTemplateAdded && nextProps.externalTemplateAdded !== externalTemplateAdded) {
      this.setState({ markupRecentlyAdded: nextProps.externalTemplateAdded });
    }
  }

  componentDidUpdate(prevProps) {
    const { name, value } = this.props;

    const {
      markupRecentlyAdded,
      textareaInfo: { cursorEnd },
    } = this.state;

    if (value !== prevProps.value && markupRecentlyAdded) {
      setTimeout(() => {
        const textArea = document.querySelector(`textarea[name="${name}"]`);
        const newCursorPosition = (cursorEnd || value.length) + markupRecentlyAdded.length + 1;

        textArea.focus();

        setTextAreaCaretPosition(textArea, newCursorPosition <= value.length ? newCursorPosition : value.length);

        this.setState({ markupRecentlyAdded: false });
      }, 0);
    }
  }

  handleInputChange = (event) => {
    const { onChange, onMentionsChange, value, cleanAllMentions } = this.props;
    const { mentions, mentionsMenuAnchor } = this.state;

    if (!mentionsMenuAnchor) {
      if (mentions && mentions.length > 0) {
        const newMentions = filter(mentions, (item) => {
          const regExp = new RegExp(`(${item.mention})`, 'g');

          return regExp.test(event.target.value);
        });

        this.setState({ mentions: newMentions });

        onMentionsChange(newMentions);
      }
    }

    // if value is empty should clean all mentions
    if (cleanAllMentions && mentions && mentions.length > 0 && value === '') {
      this.setState({ mentions: [] });
    }

    if (onChange) onChange(event);
  };

  handleInputKeyDownEvents = (event) => {
    const { onSubmit, submitOnEnter } = this.props;
    const { searchText, mentionsMenuAnchor, templatesMenuAnchor } = this.state;
    const {
      altKey,
      ctrlKey,
      shiftKey,
      keyCode,
      target: { selectionStart, value },
    } = event;
    const multiKeys = altKey || shiftKey || ctrlKey;

    // submit form when user hits 'enter'
    if (keyCode === 13 && !multiKeys && !mentionsMenuAnchor && !templatesMenuAnchor && submitOnEnter) {
      if (!isEmpty(value)) onSubmit(event);
      event.preventDefault();
    }

    if (shiftKey) {
      // open mention suggestor when user hits '@'
      if (keyCode === 50) {
        if (!mentionsMenuAnchor && !templatesMenuAnchor) {
          this.handleMenusDisplay({
            hookType: 'start',
            startPosition: selectionStart,
            key: 'mention',
          });
        } else {
          event.preventDefault();
        }
        // open templates menu when user hits '{' after another '{'.
      } else if (
        keyCode === 221 &&
        value.length > 0 &&
        value.substr(selectionStart > 0 ? selectionStart - 1 : 0, 1) === '{'
      ) {
        if (!mentionsMenuAnchor && !templatesMenuAnchor) {
          this.handleMenusDisplay({
            hookType: 'start',
            startPosition: selectionStart,
            key: 'template',
          });
        } else {
          event.preventDefault();
        }
      }
    }

    // update search text when user hits 'backspace'
    if (keyCode === 8 && !multiKeys && mentionsMenuAnchor) {
      const text = searchText.length > 1 ? searchText.substr(0, searchText.length - 1) : '';
      if (text !== searchText) {
        this.setState({ searchText: text });
      }
    }
  };

  handleInfoUpdated = (textareaInfo) => {
    const { onInfoUpdated } = this.props;

    this.setState({ textareaInfo: textareaInfo || {} });

    if (onInfoUpdated) onInfoUpdated(textareaInfo || {});
  };

  handleInputKeyPressEvents = ({ charCode }) => {
    const { searchText, mentionsMenuAnchor } = this.state;
    const char = String.fromCharCode(charCode);
    const text = `${searchText}${char}`;

    // update search text when user type something with suggestor opened
    if (mentionsMenuAnchor && text !== '@' && text !== searchText) {
      this.setState({ searchText: text });
    }
  };

  handleMenusDisplay = (metaData) => {
    const { name, variant } = this.props;
    const { hookType, key, startPosition } = metaData;

    if (key === 'mention' && variant === 'chat') {
      if (hookType === 'start') {
        this.setState({
          selectedIndex: -1,
          searchText: '',
          mentionsMenuAnchor: document.querySelector(`#input-field-component-${name}`),
          startPosition,
        });
      } else if (hookType === 'cancel') {
        this.handleStatesClear();
      }
    } else if (key === 'template') {
      if (hookType === 'start') {
        this.setState({
          searchText: '',
          templatesMenuAnchor: document.querySelector(`#input-field-component-${name}`),
          startPosition,
        });
      } else if (hookType === 'cancel') {
        this.handleStatesClear();
      }
    }
  };

  handleMentionSuggestionSelected = ({ _id, name, __typename }) => {
    const { value, onMentionsChange } = this.props;
    const { mentions, searchText, startPosition } = this.state;
    const nameTokens = split(trim(name), ' ');
    const firstname = head(nameTokens);
    const lastname = last(nameTokens);
    const type = __typename === 'Group' ? 'group' : 'user';
    const mention =
      __typename === 'Group'
        ? `@${name.replace(/\s+/g, '')}`
        : `@${capitalizeFirstLetter(firstname)}${capitalizeFirstLetter(lastname)}`;
    const prependText = value.slice(0, startPosition);
    const appendText = value.slice(startPosition + searchText.length + 1, value.length);
    const newText = `${prependText} ${mention} ${appendText}`;

    const newMention = { refId: _id, ref: type, mention };

    if (!find(mentions, { mention: newMention.mention })) mentions.push(newMention);

    this.setState({ markupRecentlyAdded: mention, mentions });

    // reset states except mentions
    this.handleStatesClear();

    onMentionsChange(mentions, newText);
  };

  handleStatesClear = () => {
    this.setState({
      mentionsMenuAnchor: false,
      templatesMenuAnchor: null,
      selectedIndex: 0,
      searchText: '',
      startPosition: null,
    });
  };

  // TODO move logic to make Lists keyboard accessible to the `components/v2/List` component
  // https://stackoverflow.com/questions/53468976/navigate-a-material-ui-list-with-arrow-keys
  handleSuggestorKeyboardEvents = (event) => {
    const { mentionsMenuAnchor, searchText, selectedIndex } = this.state;

    if (mentionsMenuAnchor) {
      switch (event.which) {
        case 8: {
          if (searchText === '') this.handleStatesClear();
          break;
        }
        case 40: {
          // down arrow
          event.preventDefault();
          const els = document.querySelectorAll('[role="tooltip"] ul [role="button"]');
          const el = get(els, selectedIndex + 1);
          if (el) {
            el.focus();
            this.setState({ selectedIndex: (selectedIndex + 1) % els.length });
          }
          break;
        }
        case 38: {
          // up arrow
          event.preventDefault();
          const els = document.querySelectorAll('[role="tooltip"] ul [role="button"]');
          const el = get(els, selectedIndex - 1);
          if (el) {
            el.focus();
            this.setState({ selectedIndex: (selectedIndex - 1) % els.length });
          }
          break;
        }
        case 27: // esc
        case 37: // left arrow
        case 39: {
          // right arrow
          event.preventDefault();
          this.handleMenusDisplay({ hookType: 'cancel', selectionStart: null, key: 'mention' });
          break;
        }
        default:
      }
    }
  };

  renderHelperText() {
    const { classes, error, helperText } = this.props;

    if (!helperText) return;

    return <Typography className={classnames(classes.helperText, { [classes.error]: error })}>{helperText}</Typography>;
  }

  renderMentionSuggestor() {
    const { searchText, mentionsMenuAnchor } = this.state;

    return (
      <PopperChatActivityAssignees
        anchorEl={mentionsMenuAnchor || undefined}
        placement="top-start"
        query={searchText}
        onClickAway={() => this.setState({ mentionsMenuAnchor: false, searchText: '' })}
        onNodeSelected={(event, node) => {
          this.setState({ mentionsMenuAnchor: false, searchText: '' });
          this.handleMentionSuggestionSelected(node);
        }}
      />
    );
  }

  renderInput() {
    const {
      classNameInput,
      disabled,
      expandable,
      readOnly,
      hintText,
      ignoreInvalidSquigglies,
      name,
      onBlur,
      onClick,
      rows,
      value,
      squigglyTemplates,
      disableScroll,
    } = this.props;
    const { mentions } = this.state;

    return (
      <InputField
        className={classNameInput}
        disabled={disabled}
        disableScroll={disableScroll}
        expandable={expandable}
        id={`input-field-component-${name}`}
        ignoreInvalidSquigglies={ignoreInvalidSquigglies}
        name={name}
        onBlur={onBlur}
        onClick={onClick}
        onChange={this.handleInputChange}
        onKeyDown={this.handleInputKeyDownEvents}
        onKeyPress={this.handleInputKeyPressEvents}
        onInfoUpdated={this.handleInfoUpdated}
        placeholder={hintText || ''}
        rows={rows || 4}
        mentions={map(mentions, 'mention') || []}
        value={value}
        squigglyTemplates={squigglyTemplates}
        readOnly={readOnly}
      />
    );
  }

  render() {
    const { classes, className } = this.props;

    return (
      <div className={classnames(className, classes.wrapper)} onKeyDown={this.handleSuggestorKeyboardEvents}>
        {this.renderInput()}
        {this.renderHelperText()}
        {this.renderMentionSuggestor()}
      </div>
    );
  }
}

EnhancedTextField.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  classNameInput: PropTypes.string,
  variant: PropTypes.string,
  disabled: PropTypes.bool,
  disableScroll: PropTypes.bool,
  expandable: PropTypes.bool,
  readOnly: PropTypes.bool,
  error: PropTypes.bool,
  externalTemplateAdded: PropTypes.string,
  ignoreInvalidSquigglies: PropTypes.bool,
  helperText: PropTypes.string,
  hintText: PropTypes.string,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onInfoUpdated: PropTypes.func,
  onMentionsChange: PropTypes.func,
  onSubmit: PropTypes.func,
  rows: PropTypes.number,
  submitOnEnter: PropTypes.bool,
  value: PropTypes.string.isRequired,
  squigglyTemplates: PropTypes.array,
  cleanAllMentions: PropTypes.bool,
};

EnhancedTextField.defaultProps = {
  variant: '',
  error: false,
  disabled: false,
  expandable: false,
  readOnly: false,
  helperText: '',
  hintText: '',
  ignoreInvalidSquigglies: false,
  onSubmit: undefined,
  rows: 4,
  submitOnEnter: false,
};

export default withStyles(styles)(EnhancedTextField);
